/*----------------------------------
- HOOK: HOME PERSONA
----------------------------------*/

/**
 * The one persona of Home's page (plan settled 4): the demo window's switch moves the value (the closing card's switch
 * left with Home v3, mock parity W3, 2026-11-04: its slider shows both views at once), written to `body[data-persona-view]` as the mock does (the sheets show the matching screenshot under it) and
 * kept for the visitor's next visit in `localStorage ud-home-persona`.
 *
 * @docs docs/features/website-landing-experience
 * @adr  ADR-0110
 * @rule SSR renders the investor view (the shipped default); the stored choice is read after mount only, so the
 *       server HTML and the first client render are the same tree (no hydration mismatch).
 * @rule Storage can be absent or throw (a private window, blocked site data): the switch then works for the visit.
 */

/*----------------------------------
- DEPENDANCES
----------------------------------*/

import { useEffect, useState } from 'preact/hooks';

import type { HomePersona } from '@uniquedomains/components/marketing/demo-window/PersonaSwitch';

/*----------------------------------
- CONSTANTS
----------------------------------*/

const STORAGE_KEY = 'ud-home-persona';
const BODY_ATTRIBUTE = 'data-persona-view';
const DEFAULT_PERSONA: HomePersona = 'investor';

/*----------------------------------
- HELPERS
----------------------------------*/

const isHomePersona = (value: string | null): value is HomePersona => value === 'investor' || value === 'founder';

/** The stored persona, or null when none is stored or storage is unavailable (a private window throws on access). */
const readStoredPersona = (): HomePersona | null => {
    try {
        const stored = window.localStorage.getItem(STORAGE_KEY);
        return isHomePersona(stored) ? stored : null;
    } catch (error) {
        // Unavailable storage is an expected state, not a failure: the visit keeps the default view.
        if (error instanceof DOMException) return null;
        throw error;
    }
};

const storePersona = (persona: HomePersona) => {
    try {
        window.localStorage.setItem(STORAGE_KEY, persona);
    } catch (error) {
        // Same as the read: a refused write only means the choice is not remembered next time.
        if (!(error instanceof DOMException)) throw error;
    }
};

/*----------------------------------
- HOOKS
----------------------------------*/

export const useHomePersona = (): [HomePersona, (persona: HomePersona) => void] => {
    const [persona, setPersona] = useState<HomePersona>(DEFAULT_PERSONA);

    useEffect(() => {
        const stored = readStoredPersona();
        if (stored) setPersona(stored);
    }, []);

    useEffect(() => {
        document.body.setAttribute(BODY_ATTRIBUTE, persona);
        return () => document.body.removeAttribute(BODY_ATTRIBUTE);
    }, [persona]);

    const choose = (next: HomePersona) => {
        setPersona(next);
        storePersona(next);
    };

    return [persona, choose];
};

export default useHomePersona;
